// Derived from: https://github.com/joshnuss/svelte-local-storage-store
// Added clear and get methods to the store

import { writable as internal, type Writable } from 'svelte/store';

declare type Persisted<T> = Writable<T> & {
    get: () => T;
    clear: () => void;
    setStorageType: (type: StorageType) => void;
};
declare type Updater<T> = (value: T) => T;
declare type StoreDict<T> = { [key: string]: Persisted<T> };

const stores: StoreDict<any> = {};

interface Serializer<T> {
    parse(text: string): T;
    stringify(object: T): string;
}

type StorageType = 'local' | 'session';

interface Options<T> {
    serializer?: Serializer<T>;
    storage?: StorageType;
}

function getStorage(type: StorageType) {
    return type === 'local' ? localStorage : sessionStorage;
}

export function persisted<T>(key: string, initialValue: T, options?: Options<T>): Persisted<T> {
    const serializer = options?.serializer ?? JSON;
    if (!options) options = {};
    if (!options.storage) options.storage = 'local';
    const useSession = () => options!.storage === 'session';
    const supportsStorage = typeof window !== 'undefined';
    const localStorage = supportsStorage ? getStorage('local') : null;
    const sessionStorage = supportsStorage ? getStorage('session') : null;
    if (localStorage?.getItem(key)) options!.storage = 'local';

    function updateStorage(key: string, value: T) {
        if (useSession()) {
            localStorage?.removeItem(key);
            if (value) sessionStorage?.setItem(key, serializer.stringify(value));
        } else {
            sessionStorage?.removeItem(key);
            if (value) localStorage?.setItem(key, serializer.stringify(value));
        }
    }

    if (!stores[key]) {
        // Eagerly read from storage so the store value is correct even before
        // any subscriber triggers the start function. Without this, calling
        // update() before the first subscription operates on initialValue and
        // can overwrite existing persisted data (e.g. recent items wiped on
        // full page reload).
        const storage = useSession() ? sessionStorage : localStorage;
        const json = storage?.getItem(key);
        let storedValue = initialValue;
        if (json) {
            try {
                storedValue = serializer.parse(json) as T;
            } catch {
                storage?.removeItem(key);
            }
        }

        if (!json) {
            updateStorage(key, initialValue);
        }

        const store = internal(storedValue);

        const { subscribe, set } = store;

        stores[key] = {
            set(value: T) {
                updateStorage(key, value);
                set(value);
            },
            update(callback: Updater<T>) {
                return store.update((last) => {
                    const value = callback(last);

                    updateStorage(key, value);

                    return value;
                });
            },
            get() {
                let value;
                subscribe((_) => (value = _))();
                return value;
            },
            clear() {
                localStorage?.removeItem(key);
                sessionStorage?.removeItem(key);
                set(initialValue);
            },
            setStorageType(type: StorageType) {
                options!.storage = type;
            },
            subscribe
        };
    }

    return stores[key];
}
